import React, { createContext, useContext, useState, useCallback } from 'react';

interface OverlayContextType {
  hasActiveOverlay: boolean;
  registerOverlay: (id: string) => void;
  unregisterOverlay: (id: string) => void;
}

const OverlayContext = createContext<OverlayContextType | undefined>(undefined);

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [overlays, setOverlays] = useState<Set<string>>(new Set());

  const registerOverlay = useCallback((id: string) => {
    setOverlays((prev) => new Set(prev).add(id));
  }, []);

  const unregisterOverlay = useCallback((id: string) => {
    setOverlays((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const hasActiveOverlay = overlays.size > 0;

  return (
    <OverlayContext.Provider value={{ hasActiveOverlay, registerOverlay, unregisterOverlay }}>
      {children}
    </OverlayContext.Provider>
  );
};

export const useOverlay = () => {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlay must be used within OverlayProvider');
  }
  return context;
};

export const useOverlayState = (isOpen: boolean, id: string) => {
  const { registerOverlay, unregisterOverlay } = useOverlay();

  React.useEffect(() => {
    if (isOpen) {
      registerOverlay(id);
    } else {
      unregisterOverlay(id);
    }
    return () => {
      unregisterOverlay(id);
    };
  }, [isOpen, id, registerOverlay, unregisterOverlay]);
};
